import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  Input,
  Row,
  Col,
  Pagination,
  Button,
  Spin,
  notification,
  Modal,
  Popconfirm,
  Breadcrumb,
  Space,
  Empty,
  Form,
  InputRef,
  Select,
  TreeSelect,
  AutoComplete,
} from "antd";
import Column from "antd/lib/table/Column";
import { Link, NavLink, RouteComponentProps } from "react-router-dom";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { v4 as uuidv4 } from "uuid";
import { Translation } from "react-i18next";
import { useSelector } from 'react-redux';
import { getOrgsOptionsSelector, treeNode } from '@redux/selector';
// API
import { CancelTokenSource, IsCancel } from "../../axios/axios.core";
import { gaEvent } from "../../withTracker";
import { TagProps } from "antd/lib";
import { getFieldConfigs, getSmartGroups, addOrUpdateSmartGroup, deleteSmartGroup, syncSmartGroup } from "../../axios/api.smartGroup";
import { FlatValueOption, OperatorOption, FieldConfig, FieldOption ,Condition,DeviceGroup } from "../../axios/models"

const SmartGroupListPage: React.FC<RouteComponentProps> = () => {
  // 列表状态
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>([]);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");

  const [allPlatformConfigs, setAllPlatformConfigs] = useState<Record<string, { fields: FieldOption[]; configs: Record<string, FieldConfig> }>>({});
  const [currentPlatform, setCurrentPlatform] = useState<string>('IOS'); // 默认平台
  const [configLoading, setConfigLoading] = useState(true);
  const orgOptions = useSelector(getOrgsOptionsSelector); 

  // Modal 状态
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const [form] = Form.useForm();
  const cancelToken = useRef(CancelTokenSource());

  // 获取列表
  const getList = async (
    newPageIndex = pageIndex,
    newPageSize = pageSize,
    searchValue = search
  ) => {
    setLoading(true);
    try {
      const res = await getSmartGroups({
        pageIndex: newPageIndex,
        pageSize: newPageSize,
        search: searchValue,
        cancelToken: cancelToken.current.token,
      });
      setData(res.dataList);
      setPageIndex(res.pageIndex);
      setPageSize(res.pageSize);
      setTotalCount(res.totalCount);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadFieldConfigs = async () => {
      try {
        setConfigLoading(true);
        const res = await getFieldConfigs();
        console.log(res);
        // setFieldOptions(res.fields || []);
        // setConfigMap(res.configs || {});
        setAllPlatformConfigs(res);
      } catch (err) {
        console.error("加载字段配置失败", err);
        //notification.error({ message: "加载字段配置失败" });
      } finally {
        setConfigLoading(false);
      }
    };
    loadFieldConfigs();
  }, []);

  useEffect(() => {
    getList();
    return () => {
      cancelToken.current.cancel("组件卸载，取消请求");
    };
  }, []);

  // 搜索
  const handleSearch = () => {
    setPageIndex(1);
    getList(1, pageSize, search);
    gaEvent("tags", "onClick", "onSearch");
  };

  // 删除
  const handleDelete = async (id: string) => {
    setLoading(true);
    try {
      await deleteSmartGroup(id);
      notification.success({ message: <Translation>{(t) => t("已删除")}</Translation> });
      getList(1);
    } catch (err) {
      //notification.error({ message: "删除失败" });
    } finally {
      setLoading(false);
    }
  };

  // 打开编辑
  const handleEdit = (item: any) => {
    setEditingId(item.id);
    setIsModalVisible(true);

    const platform = item.platform || 'IOS'; // 从 item 取
    setCurrentPlatform(platform);

    console.log(item);
    form.setFieldsValue({
      name: item.name,
      platform: platform,
      conditions: item.conditions && item.conditions.length > 0 
        ? item.conditions.map((cond: any) => ({
            field: cond.field,
            operator: cond.operator,
            value: cond.value,
            logic: cond.logic, // 如果有
          }))
        : [{}],
    });
  };

  const handleSync = async (item: any) => {
    setLoading(true);
    try {
      await syncSmartGroup(item.id);
      notification.success({ message: <Translation>{(t) => t("同步成功，请稍候查看")}</Translation> });
      getList(1);
    } catch (err) {
      //notification.error({ message: "同步失败" });
    } finally {
      setLoading(false);
    }
  };

  // 打开新增
  const handleAdd = () => {
    if (configLoading) return;
    setEditingId(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      const validConditions = (values.conditions as Condition[])
        .filter(
          (cond) =>
            cond.field !== undefined &&
            cond.operator !== undefined &&
            cond.value !== undefined
        );

      if (validConditions.length === 0) {
        notification.warning({ message: '请至少添加一个有效条件' });
        return;
      }

      const payload: DeviceGroup = {
        id: editingId || undefined,
        name: values.name,
        platform: values.platform,
        conditions: validConditions,
      };

      await addOrUpdateSmartGroup(payload); // 替换为你的实际 API

      notification.success({ message: editingId ? '更新成功' : '创建成功' });
      setIsModalVisible(false);
      form.resetFields();
      getList(1);
    } catch (err) {
      console.error(err);
      notification.error({ message: '保存失败，请重试' });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };


  // 获取当前平台配置
 //const currentConfig = allPlatformConfigs[currentPlatform == 'IOS' ? 'ios' : currentPlatform == 'Android' ? 'android' : currentPlatform ];
  const currentConfig = allPlatformConfigs[currentPlatform];
  const currentFields = currentConfig?.fields || [];
  const currentConfigs = currentConfig?.configs || {};

  return (
    <Translation>
      {(t) => (
        <div>
          <div className="breadcrumbTop py-2">
            <Breadcrumb>
              <Breadcrumb.Item>{t("设备")}</Breadcrumb.Item>
              <Breadcrumb.Item>{t("智能设备组")}</Breadcrumb.Item>
            </Breadcrumb>
            <Row style={{ padding: "32px 0 11px 0" }}>
              <Space>
                <img src="/images/tagIcon.svg" alt="" />
                <span style={{ display: "flex", fontSize: 22, fontWeight: 500, marginTop: -5 }}>
                  {t("智能设备组")}
                </span>
              </Space>
            </Row>
          </div>

          <Spin spinning={false}>
            <Row style={{ width: "100%" }}>
              {/* 搜索区域 */}
              <div className="borderBox">
                <Row className="mb-3">
                  <Col>
                    {t("名称")}:
                    <Input
                      style={{ width: "200px", marginLeft: 10 }}
                      placeholder={t("输入名称搜索")}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </Col>
                  <Col offset={1}>
                    <Button type="primary" onClick={handleSearch}>
                      {t("查询")}
                    </Button>
                  </Col>
                </Row>
              </div>

              {/* 表格区域 */}
              <div className="borderBox">
                <Row style={{ marginBottom: 8 }}>
                  <Col className="ms-auto">
                    <Button type="primary" 
                      onClick={handleAdd} 
                      disabled={configLoading} // 配置未加载完成时禁用
                    >
                      <PlusOutlined />
                      {t("添加智能设备组")}
                    </Button>
                  </Col>
                </Row>

                <Table
                  size="middle"
                  loading={loading}
                  dataSource={data}
                  rowKey="id"
                  pagination={false}
                  locale={{
                    emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("暂无数据")} />,
                  }}
                >
                  <Column key="name" dataIndex="name" title={t("名称")} />
                  <Column 
                    key="platform" 
                    dataIndex="platform" 
                    title={t("平台")} 
                    render={(v)=>{
                      if(v == "IOS")
                        return "iOS";
                      else
                        return v;
                    }}
                  />
                  <Column
                    key="deviceCount"
                    dataIndex="count"
                    title={t("设备")}
                    render={(v, record: any) => {
                        return  (
                          <Row>
                            {v === 0 ? (
                              v
                            ) : (
                              <NavLink
                                style={{ textDecoration: "underline" }}
                                to={{
                                  pathname: `/home/devices/smartGroupDevices/${record.id}`,
                                  state: { id:record.id, value: record.name, key: record.name },
                                }}
                              >
                                {v}
                              </NavLink>
                            )}
                          </Row>
                        );
                    }}
                  />
                  <Column
                    key="operation"
                    dataIndex="id"
                    title={t("操作")}
                    render={(id: string, record: any) => (
                      <Space size={8}>
                        <Button type="link" size="small" onClick={() => handleEdit(record)}>
                          {t("编辑")}
                        </Button>
                        <Popconfirm
                          title={`${t("确认删除智能设备组")} ${record.name}？`}
                          onConfirm={() => handleDelete(id)}
                          okText={t("确认")}
                          cancelText={t("取消")}
                        >
                          <Button type="link" ghost size="small">
                            {t("删除")}
                          </Button>
                        </Popconfirm>
                        <Button type="link" size="small" onClick={() => handleSync(record)}>
                          {t("同步")}
                        </Button>
                      </Space>
                    )}
                  />
                </Table>

                <Pagination
                  style={{ textAlign: "right", marginTop: "20px" }}
                  current={pageIndex}
                  pageSize={pageSize}
                  total={totalCount}
                  showSizeChanger
                  showTotal={(total, range) => `${range[0]}-${range[1]} / ${total} ${t("条")}`}
                  onChange={(page, size) => getList(page, size)}
                  onShowSizeChange={(current, size) => getList(current, size)}
                />
              </div>
            </Row>
          </Spin>

          {/* Modal 表单 */}
          <Modal
            width={700}
            maskClosable={false}
            title={editingId ? t("修改设备组") : t("添加设备组")}
            confirmLoading={loading}
            open={isModalVisible}
            onCancel={handleCancel}
            footer={null}
            destroyOnHidden
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              initialValues={{
                name: '',
                platform: 'IOS',
              }}
            >
              <Form.Item
                label="名称"
                name="name"
                rules={[{ required: true, message: '请输入设备组名称' }]}
              >
                <Input placeholder="智能设备组名称" />
              </Form.Item>

              <Form.Item
                label="平台"
                name="platform"
                rules={[{ required: true, message: '请选择平台' }]}
              >
                <Select 
                  placeholder="请选择"
                  onChange={(value: string) => {
                    setCurrentPlatform(value);
                    // 清空已有条件（因为字段可能完全不同）
                    form.setFieldsValue({ conditions: [{}] });
                  }}
                >
                  <Select.Option value="IOS">iOS</Select.Option>
                  <Select.Option value="macOS">macOS</Select.Option>
                  <Select.Option value="Android">Android</Select.Option>
                </Select>
              </Form.Item>

              {/* 动态条件列表 —— 核心修复部分 */}
              <Form.Item label="条件" required>
                <Form.List name="conditions" initialValue={[{}]}>
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...restField }, index) => {
                        return (
                          <Row gutter={8} key={key} style={{ marginBottom: 12 }}>
                            {/* 逻辑 */}
                            {index > 0 && (
                              <Col span={3}>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'logic']}
                                  noStyle
                                  initialValue="and"
                                >
                                  <Select style={{ width: '100%' }}>
                                    <Select.Option value="and">并且</Select.Option>
                                    <Select.Option value="or">或者</Select.Option>
                                  </Select>
                                </Form.Item>
                              </Col>
                            )}
                            {index == 0 && (
                              <Col span={3}>
                              </Col>
                            )}
                            {/* 字段 */}
                            <Col span={index > 0 ? 6 : 6}>
                              <Form.Item {...restField} name={[name, 'field']} noStyle>
                                <Select
                                  placeholder="字段"
                                  style={{ width: '100%' }}
                                  onChange={(value) => {
                                    // 清空 operator 和 value
                                    form.setFieldValue(['conditions', index, 'operator'], undefined);
                                    form.setFieldValue(['conditions', index, 'value'], undefined);
                                  }}
                                >
                                  {currentFields.map((opt) => (
                                    <Select.Option key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </Select.Option>
                                  ))}
                                </Select>
                              </Form.Item>
                            </Col>

                            {/* 操作符 */}
                            <Col span={4}>
                              <Form.Item
                                noStyle
                                shouldUpdate={(prev, curr) =>
                                  prev.conditions?.[index]?.field !== curr.conditions?.[index]?.field
                                }
                              >
                                {() => {
                                  const currentField = form.getFieldValue(['conditions', index, 'field']);
                                  const operators = currentConfigs[currentField]?.operators || [];
                                  return (
                                    <Form.Item
                                      {...restField}
                                      name={[name, 'operator']}
                                      noStyle
                                      rules={[{ required: true, message: '请选择操作符' }]}
                                      key={currentField || 'empty'}
                                    >
                                      <Select
                                        placeholder="操作符"
                                        disabled={!currentField || operators.length === 0}
                                        style={{ width: '100%' }}
                                      >
                                        {operators.map((op) => (
                                          <Select.Option key={op.value} value={op.value}>
                                            {op.label}
                                          </Select.Option>
                                        ))}
                                      </Select>
                                    </Form.Item>
                                  );
                                }}
                              </Form.Item>
                            </Col>
                            {/* 值 */}
                            <Col span={10}>
                              <Form.Item
                                noStyle
                                shouldUpdate={(prev, curr) =>
                                  prev.conditions?.[index]?.field !== curr.conditions?.[index]?.field ||
                                  prev.conditions?.[index]?.operator !== curr.conditions?.[index]?.operator
                                }
                              >
                                {() => {
                                  const currentField = form.getFieldValue(['conditions', index, 'field']);
                                  const currentConfig = currentField ? currentConfigs[currentField] : null;

                                  if (!currentConfig) {
                                    return (
                                      <Form.Item {...restField} name={[name, 'value']} noStyle>
                                        <Input placeholder="请选择字段" disabled />
                                      </Form.Item>
                                    );
                                  }

                                  const { type, values = [] } = currentConfig;
                                  const currentValue = form.getFieldValue(['conditions', index, 'value']);

                                  switch (type) {
                                    case 'tree':
                                      return (
                                        <Form.Item {...restField} name={[name, 'value']} noStyle>
                                          <TreeSelect
                                            treeData={orgOptions || []}
                                            placeholder={t("选择部门")}
                                            treeDefaultExpandAll
                                            showSearch
                                            style={{ width: '100%' }}
                                            filterTreeNode={(search, item) =>
                                              (item?.title as string)?.toLowerCase().includes(search.toLowerCase())
                                            }
                                          />
                                        </Form.Item>
                                      );

                                    case 'input':
                                      return (
                                        <Form.Item {...restField} name={[name, 'value']} noStyle>
                                          <Input placeholder="请输入" />
                                        </Form.Item>
                                      );

                                    case 'selectOrInput': {
                                      const currentValue = form.getFieldValue(['conditions', index, 'value']);
                                      const presetOptions = values.map(v => ({ label: v.label, value: v.value }));

                                      // 根据当前 value 找到对应的显示文本
                                      const getDisplayValue = (value: string) => {
                                        if (!value) return '';
                                        const option = presetOptions.find(opt => opt.value === value);
                                        return option ? option.label : value;
                                      };
                                      
                                      return (
                                        <AutoComplete
                                          placeholder="请选择或输入"
                                          style={{ width: '100%' }}
                                          value={getDisplayValue(currentValue)}
                                          options={presetOptions}
                                          onChange={(inputText: string) => {
                                            // 用户输入时：
                                            // 1. 如果输入的是预设选项的 label，保存对应的 value
                                            // 2. 否则，直接保存输入的内容
                                            const selectedOption = presetOptions.find(opt => opt.label === inputText);
                                            const finalValue = selectedOption ? selectedOption.value : inputText;
                                            
                                            const conditions = form.getFieldValue('conditions') || [];
                                            conditions[index] = { ...conditions[index], value: finalValue };
                                            form.setFieldsValue({ conditions });
                                          }}
                                          onSelect={(val: string, option) => {
                                            // 选择下拉选项时，直接使用 option 的 value
                                            const conditions = form.getFieldValue('conditions') || [];
                                            conditions[index] = { ...conditions[index], value: option.value };
                                            form.setFieldsValue({ conditions });
                                          }}
                                          filterOption={(input, option) =>
                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                          }
                                          allowClear
                                          onClear={() => {
                                            const conditions = form.getFieldValue('conditions') || [];
                                            conditions[index] = { ...conditions[index], value: '' };
                                            form.setFieldsValue({ conditions });
                                          }}
                                        />
                                      );
                                    }

                                    case 'flat':
                                    default:
                                      return (
                                        <Form.Item {...restField} name={[name, 'value']} noStyle>
                                          <Select
                                            placeholder="值"
                                            showSearch
                                            style={{ width: '100%' }}
                                            options={values.map((v) => ({
                                              label: v.label,
                                              value: v.value,
                                            }))}
                                            filterOption={(input, option) =>
                                              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                            }
                                          />
                                        </Form.Item>
                                      );
                                  }
                                }}
                              </Form.Item>
                            </Col>
                            {/* 删除 */}
                            <Col span={1}>
                              {fields.length > 1 && (
                                <MinusCircleOutlined
                                  style={{
                                    fontSize: '18px',
                                    color: '#ff4d4f',
                                    cursor: 'pointer',
                                    marginTop: 8,
                                  }}
                                  onClick={() => remove(name)}
                                />
                              )}
                            </Col>
                          </Row>
                        );
                      })}

                      <Button
                        type="dashed"
                        onClick={() => add({})}
                        icon={<PlusOutlined />}
                        block
                        style={{ marginBottom: 16 }}
                      >
                        {t("添加")}
                      </Button>
                    </>
                  )}
                </Form.List>
              </Form.Item>

              <Form.Item wrapperCol={{ span: 24 }} style={{ textAlign: 'right' }}>
                <Space>
                  <Button onClick={handleCancel}>取消</Button>
                  <Button type="primary" htmlType="submit" loading={loading}>
                    {t("保存")}
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Modal>
        </div>
      )}
    </Translation>
  );
};

export default SmartGroupListPage;